import React, { useEffect, useState, useRef } from 'react';
import { Spin, Modal } from 'antd';
import type { GraphNode, GraphData } from '@packages/shared';
import {
  useNetworkManager,
  useSearch,
  usePerformanceMode,
  useDataManager
} from './hooks';
import ControlPanel from './ControlPanel';
import DataPanel from './DataPanel';
import TestDataGenerator from './TestDataGenerator';
import SimpleTest from './SimpleTest';
import NetworkTest from './NetworkTest';
import { ExpandIcon, CollapseIcon, SearchIcon } from './icons';

interface KnowledgeGraphProps {
  externalData?: GraphData;
  debug?: boolean;
}

const KnowledgeGraph: React.FC<KnowledgeGraphProps> = ({ 
  externalData, 
  debug = false 
}) => {
  const [showDataPanel, setShowDataPanel] = useState(false);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [enableSearchGrayOut, setEnableSearchGrayOut] = useState(true);
  const [connectionDepth, setConnectionDepth] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pendingDataRef = useRef<{ nodes: number; edges: number } | null>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  // 数据管理
  const {
    loading,
    selectedDataType,
    nodeCount,
    edgeCount,
    currentData,
    generateNewData,
    generateNewDataDebounced,
    generateDataWithParams,
    updateDataType,
    updateNodeCount,
    updateEdgeCount,
    setTestData,
    copyDataToClipboard,
    downloadData
  } = useDataManager({
    // 移除onDataGenerated回调，避免重复更新
    externalData
  });

  // 性能模式管理
  const {
    performanceMode,
    performanceThreshold,
    autoPerformanceMode,
    shouldUseHighPerformance,
    performanceInfo,
    updatePerformanceMode,
    updatePerformanceThreshold,
    updateAutoPerformanceMode
  } = usePerformanceMode(nodeCount, {
    // 移除onModeChange回调，通过useEffect统一处理
  });

  // 主网络管理
  const {
    containerRef,
    initNetwork,
    updateData,
    updateOptions,
    focusOnNode,
    destroyNetwork
  } = useNetworkManager({
    onNodeClick: (node) => {
      setSelectedNode(node);
      // 点击空白区域时重置置灰效果，像Vue demo一样
      if (!node && currentHighlightedNode) {
        console.log('Clicked empty space, resetting gray out effect');
        resetSearchGrayOut();
      }
    }
  });

  // 全屏网络管理
  const {
    containerRef: fullscreenContainerRefInternal,
    initNetwork: initFullscreenNetwork,
    updateData: updateFullscreenData,
    updateOptions: updateFullscreenOptions,
    focusOnNode: focusOnFullscreenNode,
    destroyNetwork: destroyFullscreenNetwork
  } = useNetworkManager({
    onNodeClick: (node) => {
      setSelectedNode(node);
      if (!node && currentHighlightedNode) {
        resetSearchGrayOut();
      }
    }
  });

  // 搜索功能
  const {
    searchQuery,
    searchResults,
    currentHighlightedNode,
    statistics,
    handleSearch,
    focusOnNode: searchFocusOnNode,
    applySearchGrayOut,
    resetSearchGrayOut,
    setSearchQuery,
    setSearchResults
  } = useSearch(currentData, {
    enableGrayOut: enableSearchGrayOut,
    connectionDepth,
    onFocusNode: (nodeId) => {
      if (enableSearchGrayOut) {
        const grayedData = applySearchGrayOut(nodeId);
        // 先更新数据再定位，确保置灰效果和定位都生效
        if (isFullscreen) {
          updateFullscreenData(grayedData);
        } else {
          updateData(grayedData);
        }
      }
      // 无论是否启用置灰，都要定位到目标节点
      if (isFullscreen) {
        focusOnFullscreenNode(nodeId, shouldUseHighPerformance);
      } else {
        focusOnNode(nodeId, shouldUseHighPerformance);
      }
    },
    onResetGrayOut: () => {
      // 只调用updateData，不触发网络重建
      if (isFullscreen) {
        updateFullscreenData(currentData);
      } else {
        updateData(currentData);
      }
    }
  });

  // 初始化数据和网络
  useEffect(() => {
    const initializeGraph = async () => {
      // 如果有外部数据，不需要生成默认数据
      // 如果没有外部数据且不是debug模式，也不生成数据（让外部控制）
      // 只有在debug模式且没有外部数据时，才生成默认数据
      if (!externalData && debug) {
        await generateDataWithParams(20, 30, 'social');
      }
    };
    initializeGraph();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log('Main useEffect triggered:', {
      hasData: currentData.nodes.length > 0,
      hasContainer: !!containerRef.current,
      nodeCount: currentData.nodes.length
    });

    if (currentData.nodes.length > 0 && containerRef.current) {
      // 添加延迟确保容器完全渲染
      setTimeout(() => {
        if (containerRef.current) {
          console.log('About to initialize network...');
          try {
            initNetwork(currentData, shouldUseHighPerformance);
          } catch (error) {
            console.error('Network initialization failed in main effect:', error);
          }
        }
      }, 100);
    }
  }, [currentData, shouldUseHighPerformance]); // 移除initNetwork依赖避免循环

  // 全屏模式网络初始化
  useEffect(() => {
    if (isFullscreen && currentData.nodes.length > 0) {
      // 延迟确保容器完全渲染和ref设置完成
      const timer = setTimeout(() => {
        if (fullscreenContainerRef.current) {
          console.log('About to initialize fullscreen network...', fullscreenContainerRef.current);
          
          // 首先清理现有的全屏网络实例（如果存在）
          try {
            destroyFullscreenNetwork();
          } catch (error) {
            console.log('No existing fullscreen network to destroy');
          }
          
          // 确保内部ref指向正确的容器
          (fullscreenContainerRefInternal as any).current = fullscreenContainerRef.current;
          
          // 再次延迟确保清理完成后再初始化
          setTimeout(() => {
            try {
              console.log('Initializing fresh fullscreen network with data:', currentData.nodes.length, 'nodes');
              initFullscreenNetwork(currentData, shouldUseHighPerformance);
              console.log('Fullscreen network initialized successfully');
            } catch (error) {
              console.error('Failed to initialize fullscreen network:', error);
            }
          }, 100);
        } else {
          console.warn('Fullscreen container ref not available');
        }
      }, 300); // 增加延迟时间确保Modal完全渲染

      return () => clearTimeout(timer);
    }
    
    // 清理全屏网络
    if (!isFullscreen) {
      try {
        destroyFullscreenNetwork();
        console.log('Fullscreen network destroyed');
      } catch (error) {
        console.error('Error destroying fullscreen network:', error);
      }
    }
  }, [isFullscreen, currentData, shouldUseHighPerformance]); // 全屏模式的网络初始化

  // 处理待处理的数据请求
  useEffect(() => {
    if (!loading && pendingDataRef.current) {
      const { nodes, edges } = pendingDataRef.current;
      console.log('Processing pending data:', { nodes, edges });
      pendingDataRef.current = null;
      // 直接调用生成方法，避免递归
      generateDataWithParams(nodes, edges);
    }
  }, [loading, generateDataWithParams]);

  // 清理
  useEffect(() => {
    return () => {
      destroyNetwork();
      destroyFullscreenNetwork();
    };
  }, [destroyNetwork, destroyFullscreenNetwork]);

  // 处理搜索结果点击
  const handleSearchResultClick = (nodeId: string) => {
    searchFocusOnNode(nodeId);
    setSearchQuery('');
    setSearchResults([]);
  };

  // 处理重置搜索
  const handleResetSearch = () => {
    resetSearchGrayOut();
  };

  // 处理生成新数据
  const handleGenerateNewData = async () => {
    await generateNewData();
  };

  // 处理测试数据设置
  const handleSetTestData = async (nodes: number, edges: number) => {
    if (loading) {
      console.log('Already loading, storing pending data:', { nodes, edges });
      pendingDataRef.current = { nodes, edges };
      return; // 如果正在加载中，存储待处理的数据
    }
    
    console.log('Setting test data immediately:', { nodes, edges });
    pendingDataRef.current = null; // 清除待处理数据
    
    try {
      // 直接使用参数生成数据，避免React状态异步更新问题
      await generateDataWithParams(nodes, edges);
      console.log('Test data set successfully');
    } catch (error) {
      console.error('Failed to set test data:', error);
    }
  };

  // 处理全屏切换
  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 合并fullscreenContainerRef和fullscreenContainerRefInternal
  useEffect(() => {
    if (isFullscreen && fullscreenContainerRef.current) {
      // 确保全屏网络管理器使用正确的容器引用
      (fullscreenContainerRefInternal as any).current = fullscreenContainerRef.current;
    }
  }, [isFullscreen]);

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      {/* Debug模式下的控制面板 */}
      {debug && (
        <ControlPanel
          selectedDataType={selectedDataType}
          nodeCount={nodeCount}
          edgeCount={edgeCount}
          enableSearchGrayOut={enableSearchGrayOut}
          connectionDepth={connectionDepth}
          performanceMode={performanceMode}
          autoPerformanceMode={autoPerformanceMode}
          performanceThreshold={performanceThreshold}
          showDataPanel={showDataPanel}
          loading={loading}
          debug={debug}
          onDataTypeChange={updateDataType}
          onNodeCountChange={updateNodeCount}
          onEdgeCountChange={updateEdgeCount}
          onEnableSearchGrayOutChange={setEnableSearchGrayOut}
          onConnectionDepthChange={setConnectionDepth}
          onPerformanceModeChange={updatePerformanceMode}
          onAutoPerformanceModeChange={updateAutoPerformanceMode}
          onPerformanceThresholdChange={updatePerformanceThreshold}
          onSetTestData={handleSetTestData}
          onGenerateNewData={handleGenerateNewData}
          onToggleDataPanel={() => setShowDataPanel(!showDataPanel)}
        />
      )}

      {/* 主内容区域 */}
      <div className={`flex-1 flex min-h-0 ${debug ? '' : 'h-full'}`}>
        {/* 图谱容器 */}
        <div className="flex-1 relative">
          <div
            ref={containerRef}
            className="absolute inset-0"
            style={{ 
              background: '#ffffff',
              overflow: 'hidden'
            }}
          />

          {/* 右上角控制按钮 */}
          {debug ? (
            // Debug模式：显示搜索面板
            <div className="absolute top-4 right-4 z-10">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="搜索节点..."
                    className="w-48 px-3 py-2 text-sm border-none rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm"
                    style={{ backgroundColor: '#F2F7FB', borderRadius: '8px' }}
                  />
                  {searchQuery && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto mt-1">
                      {searchResults.length > 0 ? (
                        searchResults.map((result) => (
                          <div
                            key={result.id}
                            onClick={() => handleSearchResultClick(result.id)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                          >
                            <span className="font-medium">{result.label}</span>
                            <span className="text-gray-500 ml-2">({result.type})</span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          暂无搜索结果
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* 重置按钮 */}
                {currentHighlightedNode && (
                  <button
                    onClick={handleResetSearch}
                    className="px-3 py-1.5 text-sm bg-transparent hover:bg-gray-50 text-gray-600 hover:text-gray-800 rounded-lg border-none transition-colors cursor-pointer"
                    style={{ backgroundColor: '#F2F7FB', borderRadius: '8px' }}
                    title="重置置灰效果"
                  >
                    重置
                  </button>
                )}
              </div>
            </div>
          ) : (
            // 非Debug模式：显示放大按钮
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={handleToggleFullscreen}
                className="p-0 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                title="放大知识图谱"
              >
                <ExpandIcon />
              </button>
            </div>
          )}

          {/* 加载状态 */}
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
              <div className="text-center">
                <Spin size="large" />
                <div className="text-sm text-gray-600 mt-2">正在生成图谱...</div>
              </div>
            </div>
          )}
        </div>

        {/* 右侧数据面板 */}
        <DataPanel
          visible={showDataPanel}
          currentData={currentData}
          statistics={statistics}
          performanceInfo={performanceInfo}
          onCopyData={copyDataToClipboard}
          onDownloadData={downloadData}
        />
      </div>


      {/* 全屏弹层 */}
      <Modal
        open={isFullscreen}
        onCancel={handleToggleFullscreen}
        footer={null}
        width="90vw"
        centered={true}
        style={{ maxWidth: 'none' }}
        styles={{ 
          body: {
            padding: 0, 
            height: '80vh',
            position: 'relative',
            backgroundColor: '#f8fafc'
          }
        }}
        closable={false}
        maskClosable={false}
        destroyOnHidden={false}
      >
        <div className="relative w-full h-full">
          {/* 全屏图谱容器 */}
          <div
            ref={fullscreenContainerRef}
            className="absolute inset-0"
            style={{ 
              background: '#ffffff',
              overflow: 'hidden'
            }}
          />

          {/* 全屏模式右上角控制按钮 */}
          <div className="absolute top-4 right-4 z-10">
            <div className="flex items-center gap-3">
              {/* 搜索框 */}
              <div className="relative">
                <div className="relative flex items-center">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="请输入"
                    className="w-64 h-10 pl-10 pr-4 text-sm border-none focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm transition-all"
                    style={{ backgroundColor: '#F2F7FB', borderRadius: '8px' }}
                  />
                </div>
                {searchQuery && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto mt-1">
                    {searchResults.length > 0 ? (
                      searchResults.map((result) => (
                        <div
                          key={result.id}
                          onClick={() => handleSearchResultClick(result.id)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 flex items-center justify-between"
                        >
                          <div>
                            <span className="font-medium text-gray-800">{result.label}</span>
                            <span className="text-gray-500 ml-2 text-xs">({result.type})</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        暂无搜索结果
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* 重置按钮 */}
              {currentHighlightedNode && (
                <button
                  onClick={handleResetSearch}
                  className="h-10 px-4 text-sm bg-transparent hover:bg-gray-50 text-gray-600 hover:text-gray-800 border-none shadow-sm transition-colors cursor-pointer"
                  style={{ backgroundColor: '#F2F7FB', borderRadius: '8px' }}
                  title="重置置灰效果"
                >
                  重置
                </button>
              )}
              
              {/* 缩小按钮 */}
              <button
                onClick={handleToggleFullscreen}
                className="p-0 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                title="退出全屏"
              >
                <CollapseIcon />
              </button>
            </div>
          </div>

          {/* 全屏加载状态 */}
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
              <div className="text-center">
                <Spin size="large" />
                <div className="text-sm text-gray-600 mt-2">正在生成图谱...</div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default KnowledgeGraph;